'use client';

import { useEffect, useRef, useState } from 'react';
import { buildIcoBlob, ICO_SIZE_OPTIONS } from '@/lib/browser-ico-encoder';

export default function BrowserIcoConverter() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('icon');
  const [sizes, setSizes] = useState<number[]>([16, 32, 48, 256]);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const previewUrlRef = useRef<string | null>(null);
  const downloadUrlRef = useRef<string | null>(null);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    downloadUrlRef.current = downloadUrl;
  }, [downloadUrl]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }
    };
  }, []);

  function resetDownload() {
    setDownloadUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
  }

  function handleFile(file: File | undefined) {
    if (!file) {
      return;
    }
    setError(null);
    resetDownload();
    setFileName(file.name.replace(/\.[^.]+$/, '') || 'icon');
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setPreviewUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return url;
      });
    };
    img.onerror = () => {
      setError('This image could not be read. Try a PNG or JPG file.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  function toggleSize(size: number) {
    resetDownload();
    setSizes((prev) =>
      prev.includes(size) ? prev.filter((value) => value !== size) : [...prev, size],
    );
  }

  async function buildIco() {
    if (!image) {
      setError('Choose a PNG or JPG image first.');
      return;
    }
    if (!sizes.length) {
      setError('Pick at least one icon size to include.');
      return;
    }
    setIsBuilding(true);
    setError(null);
    try {
      const blob = await buildIcoBlob(image, sizes);
      setDownloadUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return URL.createObjectURL(blob);
      });
    } catch (buildError) {
      setError(
        buildError instanceof Error ? buildError.message : 'The ICO file could not be built.',
      );
    } finally {
      setIsBuilding(false);
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">ICO</span>
        <h2 className="tool-live-title">Convert a PNG or JPG to an ICO icon</h2>
        <p className="tool-live-subtitle">
          Upload a PNG or JPG, choose the icon sizes to include, and download a multi-size Windows
          .ico file, entirely in your browser. Nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose a PNG or JPG image</span>
            <input
              accept="image/png,image/jpeg"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          <div className="tool-live-controls">
            {ICO_SIZE_OPTIONS.map((size) => (
              <label className="tool-live-field" key={size}>
                <span>
                  <input
                    checked={sizes.includes(size)}
                    onChange={() => toggleSize(size)}
                    type="checkbox"
                  />{' '}
                  {size}×{size}
                </span>
              </label>
            ))}
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!image || !sizes.length || isBuilding}
              onClick={() => void buildIco()}
              type="button"
            >
              {isBuilding ? 'Building…' : 'Build ICO'}
            </button>
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}.ico`}
                href={downloadUrl}
              >
                Download ICO
              </a>
            ) : null}
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {image || error ? null : (
            <p className="tool-live-panel-note">
              Pick a square image for the sharpest icon at every size.
            </p>
          )}
        </section>

        <section className="tool-live-preview">
          {previewUrl ? (
            <img
              alt="Icon source preview"
              src={previewUrl}
              style={{ maxWidth: '100%', height: 'auto', borderRadius: 12 }}
            />
          ) : null}
          <p className="tool-live-panel-note">
            The ICO is packed with PNG-encoded images and its header is verified before download.
            Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
